<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="8"
          md="8"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="4"
          md="4"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="filter"
              class="d-inline-block mr-1"
              :placeholder="$t('Search...')"
              type="search"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      :items="publicHolidayList.config"
      responsive
      :busy="isBusy"
      :fields="translatedTableHeaders"
      :filter="filter"
      :filter-included-fields="filterOn"
      filter-debounce="100"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
      @filtered="onFiltered"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(date)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
              @click="onSelectPublicHolidayItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span
                class="align-middle ml-50"
              > {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectPublicHolidayItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeletePublicHoliday(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50"> {{ $t('Delete') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="md"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="publicHolidayRules">
        <b-form>
          <b-row class="match-height">
            <!-- Public Name -->
            <b-col md="12">
              <InputField
                id="public-name"
                v-model="publicHolidayFormData.name"
                :label="$t('Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Name')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Public Name -->
          </b-row>
          <b-row class="match-height">
            <!-- frequency -->
            <b-col md="12">
              <SelectField
                id="frequency"
                v-model="publicHolidayFormData.frequency"
                :reduce="option => option.value"
                :label="$t('Frequency')"
                :placeholder="$t('Select')"
                :options="translatedFrequencyOptions"
                :error-name="$t('Frequency')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- frequency -->
          </b-row>
          <b-row class="match-height">
            <!-- code -->
            <b-col md="12">
              <DatePickerField
                id="date-of-public-holiday"
                v-model="publicHolidayFormData.date"
                rules="required"
                :label="$t('Date Of Public Holiday')"
                format="d/m/Y"
                :config="datePickerConfig"
                :disabled="isReadonly"
              />
            </b-col>
            <!-- code -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BTable,
  BDropdown, BDropdownItem, BModal, BSpinner, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import Button from '../../../../components/button/Button.vue'

const frequencyOptions = [
  { label: 'Continuous', value: 'Continuous' },
  { label: 'Non-continuous', value: 'Non-continuous' },
]
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    DatePickerField,
    SelectField,
    TablePagination,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      sortBy: 'name',
      required,
      filterOn: [],
      filter: null,
      modalTitle: this.$t('Create New Public Holiday'),
      okTitle: this.$t('Save'),
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'frequency', sortable: true, label: 'Frequency' },
        { key: 'date', sortable: true, label: 'Date' },
        { key: 'actions', label: 'Actions' },
      ],
      publicHolidayFormData: {
        name: '',
        frequency: '',
        date: null,
        id: '',
      },
      publicHolidayList: {
        config: [],
      },
      // frequencyOptions: [
      //   'Continuous',
      //   'Non-continuous',
      // ],
      toDateWithYear,
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedFrequencyOptions() {
      return frequencyOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  mounted() {
    this.fetchPublicHoliday()
  },
  methods: {
    ...mapActions({
      getPublicHoliday: 'operations/getPublicHolidayList',
      createPublicHoliday: 'operations/createPublicHolidayAction',
      updatePublicHoliday: 'operations/updatePublicHolidayAction',
      deletePublicHoliday: 'operations/deletePublicHolidayAction',
    }),

    async publicHolidayAction() {
      const yourRequest = this.$t('Your request to')
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createPublicHoliday({
            config: {
              ...this.publicHolidayFormData,
            },
          })
          : await this.updatePublicHoliday({
            config: {
              ...this.publicHolidayFormData,
            },
          })
        const { status } = await response
        if (status === 200) {
          const text1 = this.$t('Working Days was successful')
          this.fetchPublicHoliday()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${text1}` })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const reasonText = this.$t('Working Days failed. Reason:')
        this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${reasonText} ${error}` })
      } finally {
        this.isLoading = false
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.publicHolidayRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisAction = this.$t('this Public Holiday?')
          this.confirm(this.publicHolidayAction, { text: `${areYouSure} ${this.buttonLabel} ${thisAction}` })
        }
      })
    },
    async fetchPublicHoliday() {
      try {
        this.isBusy = true
        const response = await this.getPublicHoliday()
        const { status, data } = response
        if (status === 200 && data.data !== null) {
          this.publicHolidayList.config = data.data
          this.totalCount = this.publicHolidayList.config.length
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onDeletePublicHoliday(item) {
      const deletePublicHoliday = async () => {
        try {
          this.showSpinner = true
          const response = await this.deletePublicHoliday(item.id)
          const { status } = response
          if (status === 200) {
            this.fetchPublicHoliday()
            this.alertSuccess({ text: this.$t('Deleted successfully') })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deletePublicHoliday, { text: this.$t('Are you sure you want to delete this Public Holiday?') })
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalCount = filteredItems.length
      this.currentPage = 1
    },
    resetModal() {
      this.publicHolidayFormData.name = ''
      this.publicHolidayFormData.frequency = ''
      this.publicHolidayFormData.date = ''
      this.isLoading = false
      this.buttonLabel = this.$t('Save')
    },
    async onSelectPublicHolidayItem(items, activity, val) {
      this.publicHolidayFormData.name = items.name
      this.publicHolidayFormData.frequency = items.frequency
      this.publicHolidayFormData.date = items.date
      this.publicHolidayFormData.id = items.id
      this.datePickerConfig.defaultDate = items.date
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Public Holiday') : this.$t('Public Holiday')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
